import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtMoney, fmtDate, STATUS_LABELS, STATUS_STYLES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileDown } from "lucide-react";

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const year = new Date().getFullYear();
  const [start, setStart] = useState(`${year}-01-01`);
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async (s, e) => {
    try {
      const { data } = await api.get("/reports/revenue-summary", { params: { start: s, end: e } });
      setData(data);
    } catch (err) { toast.error(apiError(err)); }
  }, []);

  useEffect(() => { load(start, end); }, [load, start, end]);

  const preset = (s, e) => { setStart(s); setEnd(e); };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/exports/transactions", { params: { start, end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jbm-transactions-${start || "all"}-${end || "today"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your spreadsheet is downloading.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const t = data?.totals;

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Reports & Export</h1>
        <p className="text-muted-foreground mt-2">A one-page revenue summary your CPA can work from, plus a full spreadsheet export.</p>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-6 flex flex-wrap items-end gap-4">
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => preset(`${year}-01-01`, "")} data-testid="preset-this-year">This year</Button>
            <Button variant="secondary" size="sm" onClick={() => preset(`${year - 1}-01-01`, `${year - 1}-12-31`)} data-testid="preset-last-year">Last year</Button>
            <Button variant="secondary" size="sm" onClick={() => preset("", "")} data-testid="preset-all-time">All time</Button>
          </div>
          <div className="space-y-1.5">
            <Label>From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
          </div>
          <div className="space-y-1.5">
            <Label>To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
          </div>
          {isAdmin && (
            <Button onClick={download} disabled={downloading} data-testid="export-csv-btn">
              <FileDown className="h-4 w-4 mr-2" /> {downloading ? "Preparing…" : "Download full spreadsheet (CSV)"}
            </Button>
          )}
        </CardContent>
      </Card>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["Net revenue", fmtMoney(t.net_total), "After refunds; cancelled orders excluded", "totals-net"],
            ["Orders", t.order_count.toLocaleString(), `${t.cancelled_count} cancelled (not counted)`, "totals-orders"],
            ["Refunds given", fmtMoney(t.refunds), null, "totals-refunds"],
            ["Shipping + tax collected", fmtMoney((t.shipping || 0) + (t.tax || 0)), `Shipping ${fmtMoney(t.shipping)} · Tax ${fmtMoney(t.tax)}`, "totals-shipping-tax"],
          ].map(([label, value, sub, tid]) => (
            <Card key={label} className="shadow-sm" data-testid={tid}>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="text-2xl font-serif font-semibold mt-1">{value}</p>
                {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="font-serif text-2xl">Revenue by category</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table data-testid="category-table">
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Items sold</TableHead>
                <TableHead className="text-right">Sales</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data || data.categories.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No sales in this period.</TableCell></TableRow>
              ) : (
                data.categories.map((c) => (
                  <TableRow key={c.category} data-testid={`category-row-${c.category}`}>
                    <TableCell className="py-4 font-medium">{c.category}</TableCell>
                    <TableCell className="text-right">{c.items_sold}</TableCell>
                    <TableCell className="text-right font-medium">{fmtMoney(c.gross_sales)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="font-serif text-2xl">Largest transactions</CardTitle>
          <p className="text-sm text-muted-foreground">The biggest orders in this period, largest first.</p>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="top-transactions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="hidden md:table-cell">Order</TableHead>
                <TableHead className="hidden sm:table-cell">From</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data || !data.top_transactions || data.top_transactions.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No transactions in this period.</TableCell></TableRow>
              ) : (
                data.top_transactions.map((t2) => (
                  <TableRow key={t2.id} data-testid={`top-transaction-row-${t2.id}`}>
                    <TableCell className="py-3 text-muted-foreground whitespace-nowrap">{fmtDate(t2.order_date)}</TableCell>
                    <TableCell>
                      <Link to={`/customers/${t2.customer_id}`} className="font-medium hover:text-primary hover:underline">{t2.customer_name}</Link>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{t2.source_order_id}</TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{t2.source_platform}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[t2.status] || ""}`}>{STATUS_LABELS[t2.status] || t2.status}</span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {fmtMoney(t2.total)}
                      {t2.refund_amount > 0 && <p className="text-xs text-destructive font-normal">−{fmtMoney(t2.refund_amount)} refunded</p>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {t && (
        <Card className="shadow-sm">
          <CardHeader><CardTitle className="font-serif text-2xl">Order totals breakdown</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 text-sm" data-testid="totals-breakdown">
              {[
                ["Product subtotals", fmtMoney(t.subtotal)],
                ["Shipping", fmtMoney(t.shipping)],
                ["Tax", fmtMoney(t.tax)],
                ["Discounts given", `−${fmtMoney(t.discounts)}`],
                ["Gross total", fmtMoney(t.gross_total)],
                ["Refunds", `−${fmtMoney(t.refunds)}`],
                ["Cancelled orders (excluded)", `${t.cancelled_count} · ${fmtMoney(t.cancelled_amount)}`],
                ["Net total", fmtMoney(t.net_total)],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="font-semibold text-base mt-0.5">{value}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
